import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  NumberInput,
  Text,
  Title,
} from '@mantine/core';
import useChannelsStore from '../store/channels';
import API from '../api';
import useLogosStore from '../store/logos';
import useStreamProfilesStore from '../store/streamProfiles';
import useBrowserStorage from '../hooks/useBrowserStorage';
import SystemEvents from '../components/SystemEvents';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import {
  fetchAllConnectionStats,
  getClientStats,
  getCatchupPrograms,
  getCombinedConnections,
  getCurrentPrograms,
  getStatsByChannelId,
  stopChannel,
  stopClient,
  stopTimeshiftSession,
  stopVODClient,
} from '../utils/pages/StatsUtils.js';
import {
  computeCatchupArchivePositionSecs,
  computeCatchupPlaybackSeconds,
  isCatchupPlayheadOutsideProgram,
} from '../utils/cards/TimeshiftConnectionCardUtils.js';
const VodConnectionCard = React.lazy(
  () => import('../components/cards/VodConnectionCard.jsx')
);
const TimeshiftConnectionCard = React.lazy(
  () => import('../components/cards/TimeshiftConnectionCard.jsx')
);
const StreamConnectionCard = React.lazy(
  () => import('../components/cards/StreamConnectionCard.jsx')
);

const Connections = ({
  combinedConnections,
  clients,
  channelsByUUID,
  channels,
  handleStopVODClient,
  handleStopTimeshiftSession,
  currentPrograms,
  catchupPrograms,
}) => {
  return combinedConnections.length === 0 ? (
    <Box
      ta="center"
      p={40}
      style={{
        gridColumn: '1 / -1',
      }}
    >
      <Text size="xl" c="dimmed">
        No active connections
      </Text>
    </Box>
  ) : (
    <ErrorBoundary inline>
      <Suspense fallback={<LoadingOverlay />}>
        {combinedConnections.map((connection) => {
          if (connection.type === 'stream') {
            return (
              <StreamConnectionCard
                key={connection.id}
                channel={connection.data}
                clients={clients}
                stopClient={stopClient}
                stopChannel={stopChannel}
                channelsByUUID={channelsByUUID}
                channels={channels}
                currentProgram={currentPrograms[connection.data.channel_id]}
              />
            );
          } else if (connection.type === 'vod') {
            return (
              <VodConnectionCard
                key={connection.id}
                vodContent={connection.data}
                stopVODClient={handleStopVODClient}
              />
            );
          } else if (connection.type === 'timeshift') {
            return (
              <TimeshiftConnectionCard
                key={connection.id}
                timeshiftSession={connection.data}
                currentProgram={catchupPrograms[connection.data.session_id]}
                stopTimeshiftSession={handleStopTimeshiftSession}
              />
            );
          }
          return null;
        })}
      </Suspense>
    </ErrorBoundary>
  );
};

const StatsPage = () => {
  const channelStats = useChannelsStore((s) => s.stats);
  const setChannelStats = useChannelsStore((s) => s.setChannelStats);
  const vodConnections = useChannelsStore((s) => s.activeVodConnections);
  const setVodStats = useChannelsStore((s) => s.setVodStats);
  const timeshiftSessions = useChannelsStore((s) => s.activeTimeshiftSessions);
  const setTimeshiftStats = useChannelsStore((s) => s.setTimeshiftStats);
  const enableLogoRendering = useLogosStore((s) => s.enableLogoRendering);
  const streamProfiles = useStreamProfilesStore((s) => s.profiles);

  const [clients, setClients] = useState([]);
  const [channelHistory, setChannelHistory] = useState({});
  const [isPollingActive, setIsPollingActive] = useState(false);
  const [currentPrograms, setCurrentPrograms] = useState({});
  const [catchupPrograms, setCatchupPrograms] = useState({});
  const [channels, setChannels] = useState({}); // id -> channel
  const [channelsByUUID, setChannelsByUUID] = useState({}); // uuid -> id

  useEffect(() => {
    enableLogoRendering();
  }, [enableLogoRendering]);

  // Compute needed channel UUIDs from the current active channels.
  // Stream previews use a non-UUID hash as channel_id; filter those out.
  const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const neededUUIDs = useMemo(
    () => Object.keys(channelHistory || {}).filter((id) => UUID_REGEX.test(id)),
    [channelHistory]
  );

  // Keep a ref so the programs poller always has the latest valid UUIDs
  const neededUUIDsRef = useRef(neededUUIDs);
  useEffect(() => {
    neededUUIDsRef.current = neededUUIDs;
  }, [neededUUIDs]);

  // Fetch any missing channels by UUID when the needed set changes (for card name/logo)
  useEffect(() => {
    if (!neededUUIDs || neededUUIDs.length === 0) return;
    const missing = neededUUIDs.filter((u) => channelsByUUID[u] === undefined);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await API.getChannelsByUUIDs(missing);
        if (cancelled) return;
        if (Array.isArray(res)) {
          setChannels((prev) => {
            const next = { ...prev };
            for (const ch of res) next[ch.id] = ch;
            return next;
          });
          setChannelsByUUID((prev) => {
            const next = { ...prev };
            for (const ch of res) next[ch.uuid] = ch.id;
            return next;
          });
        }
      } catch (e) {
        console.error('Failed to fetch channels by UUIDs', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [neededUUIDs.join(',')]);

  // Use localStorage for stats refresh interval (in seconds)
  const [refreshIntervalSeconds, setRefreshIntervalSeconds] = useBrowserStorage(
    'stats-refresh-interval',
    5
  );
  const refreshInterval = refreshIntervalSeconds * 1000; // Convert to milliseconds
  const channelHistoryLength = Object.keys(channelHistory).length;
  const vodConnectionsCount = vodConnections.reduce(
    (total, vodContent) => total + (vodContent.connections?.length || 0),
    0
  );
  const timeshiftConnectionsCount = timeshiftSessions.reduce(
    (total, session) => total + (session.connections?.length || 0),
    0
  );

  const handleStopVODClient = async (clientId) => {
    await stopVODClient(clientId);
    fetchAllStats();
  };

  const handleStopTimeshiftSession = async (sessionId) => {
    await stopTimeshiftSession(sessionId);
    fetchAllStats();
  };

  const fetchAllStats = useCallback(async () => {
    try {
      const response = await fetchAllConnectionStats();
      if (response) {
        if (response.live) {
          setChannelStats(response.live);
        }
        if (response.vod) {
          setVodStats(response.vod);
        }
        if (response.catchup) {
          setTimeshiftStats(response.catchup);
        }
      } else {
        console.log('Combined stats API response was empty or null');
      }
    } catch (error) {
      console.error('Error fetching connection stats:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        body: error.body,
      });
    }
  }, [setChannelStats, setVodStats, setTimeshiftStats]);

  // Always fetch once on mount, regardless of polling interval setting
  useEffect(() => {
    fetchAllStats();
  }, [fetchAllStats]);

  // Set up polling for stats when on stats page
  useEffect(() => {
    const isOnStatsPage = window.location.pathname === '/stats';

    if (isOnStatsPage && refreshInterval > 0) {
      setIsPollingActive(true);

      const interval = setInterval(() => {
        fetchAllStats();
      }, refreshInterval);

      return () => {
        clearInterval(interval);
        setIsPollingActive(false);
      };
    } else {
      setIsPollingActive(false);
    }
  }, [refreshInterval, fetchAllStats]);

  useEffect(() => {
    console.log('Processing channel stats:', channelStats);
    if (
      !channelStats ||
      !channelStats.channels ||
      !Array.isArray(channelStats.channels) ||
      channelStats.channels.length === 0
    ) {
      console.log('No channel stats available:', channelStats);
      // Clear clients and channel history when there are no stats
      setClients([]);
      setChannelHistory({});
      return;
    }

    // Use functional update to access previous state without dependency
    setChannelHistory((prevChannelHistory) => {
      // Create a completely new object based only on current channel stats
      const stats = getStatsByChannelId(
        channelStats,
        prevChannelHistory,
        channelsByUUID,
        channels,
        streamProfiles
      );

      console.log('Processed active channels:', stats);

      // Update clients based on new stats
      setClients(getClientStats(stats));

      return stats; // Return only currently active channels
    });
  }, [channelStats, channels, channelsByUUID, streamProfiles]);

  // Track which channel IDs are active (only changes when channels start/stop, not on stats updates)
  const activeChannelIds = useMemo(() => {
    return Object.keys(channelHistory).sort().join(',');
  }, [channelHistory]);

  // Smart polling for current programs - only fetch when active channels change
  useEffect(() => {
    // Skip if no active channels
    if (!activeChannelIds) {
      setCurrentPrograms({});
      return;
    }

    let timer = null;

    const fetchPrograms = async () => {
      const programs = await getCurrentPrograms(neededUUIDsRef.current);
      setCurrentPrograms(programs);

      // Schedule next fetch based on nearest program end time
      if (programs && Object.keys(programs).length > 0) {
        const now = new Date();
        let nearestEndTime = null;

        Object.values(programs).forEach((program) => {
          if (program && program.end_time) {
            const endTime = new Date(program.end_time);
            if (
              endTime > now &&
              (!nearestEndTime || endTime < nearestEndTime)
            ) {
              nearestEndTime = endTime;
            }
          }
        });

        if (nearestEndTime) {
          const timeUntilChange = nearestEndTime.getTime() - now.getTime();
          const fetchDelay = Math.max(timeUntilChange + 5000, 0);

          timer = setTimeout(fetchPrograms, fetchDelay);
        }
      }
    };

    // Initial fetch
    fetchPrograms();

    // Cleanup timer on unmount or when active channels change
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [activeChannelIds]); // Only re-run when active channel set changes

  // Catch-up programme metadata: fetch when missing or when the playhead has
  // left the cached programme's window; otherwise keep that result and only
  // do cheap local checks until the next boundary.
  const timeshiftProgrammeKey = useMemo(() => {
    return timeshiftSessions
      .map((session) => `${session.session_id}:${session.programme_start || ''}`)
      .sort()
      .join(',');
  }, [timeshiftSessions]);

  const timeshiftSessionsRef = useRef(timeshiftSessions);
  const catchupProgramsRef = useRef(catchupPrograms);
  const programmeCheckRef = useRef(() => {});
  const playbackFingerprintRef = useRef('');

  useEffect(() => {
    timeshiftSessionsRef.current = timeshiftSessions;

    const fingerprint = timeshiftSessions
      .map(
        (session) =>
          `${session.session_id}:${session.playback_base_secs ?? ''}:${session.position_anchor_at ?? ''}:${session.paused ? 1 : 0}`
      )
      .sort()
      .join(',');
    if (
      playbackFingerprintRef.current &&
      fingerprint !== playbackFingerprintRef.current
    ) {
      programmeCheckRef.current();
    }
    playbackFingerprintRef.current = fingerprint;
  }, [timeshiftSessions]);
  useEffect(() => {
    catchupProgramsRef.current = catchupPrograms;
  }, [catchupPrograms]);

  useEffect(() => {
    if (!timeshiftProgrammeKey) {
      setCatchupPrograms({});
      return;
    }

    let cancelled = false;
    let timer = null;
    let inFlight = false;

    const clearTimer = () => {
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const sessionNeedsProgrammeFetch = (session, program) => {
      if (!program?.start_time || program.duration_secs == null) {
        return true;
      }
      return isCatchupPlayheadOutsideProgram({
        programmeStart: session.programme_start,
        programStartTime: program.start_time,
        programDurationSecs: program.duration_secs,
        positionAnchorAt: session.position_anchor_at,
        playbackBaseSecs: session.playback_base_secs,
        paused: Boolean(session.paused),
      });
    };

    const msUntilNearestBoundary = () => {
      let nearestMs = null;
      timeshiftSessionsRef.current.forEach((session) => {
        const program = catchupProgramsRef.current?.[session.session_id];
        if (!program?.start_time || program.duration_secs == null) {
          return;
        }
        const position = computeCatchupPlaybackSeconds({
          programmeStart: session.programme_start,
          programStartTime: program.start_time,
          programDurationSecs: program.duration_secs,
          positionAnchorAt: session.position_anchor_at,
          playbackBaseSecs: session.playback_base_secs,
          paused: Boolean(session.paused),
          capToDuration: false,
          allowNegative: true,
        });
        if (position == null) {
          return;
        }
        if (position < 0) {
          nearestMs = 0;
          return;
        }
        const remainingMs = Math.max(
          0,
          (Number(program.duration_secs) - position) * 1000
        );
        if (nearestMs == null || remainingMs < nearestMs) {
          nearestMs = remainingMs;
        }
      });
      return nearestMs;
    };

    const scheduleCheck = () => {
      clearTimer();
      if (cancelled) {
        return;
      }

      const sessions = timeshiftSessionsRef.current;
      const programs = catchupProgramsRef.current || {};
      if (
        sessions.some((session) =>
          sessionNeedsProgrammeFetch(session, programs[session.session_id])
        )
      ) {
        fetchPrograms();
        return;
      }

      // Wake near the programme end; also at least every 2s so a seek/heartbeat
      // that jumps the playhead is noticed without hitting the API.
      const remainingMs = msUntilNearestBoundary();
      const delay =
        remainingMs == null
          ? 2000
          : Math.min(Math.max(remainingMs + 250, 250), 2000);
      timer = setTimeout(scheduleCheck, delay);
    };

    const fetchPrograms = async () => {
      if (cancelled || inFlight) {
        return;
      }
      inFlight = true;
      clearTimer();
      try {
        const existingPrograms = catchupProgramsRef.current || {};
        const sessions = timeshiftSessionsRef.current.map((session) => {
          const existing = existingPrograms[session.session_id];
          // Archive playhead relative to the URL programme (uncapped). When the
          // card has already advanced and we only have URL math, omit position
          // and let the API fall back to Redis.
          const positionSecs = computeCatchupArchivePositionSecs({
            programmeStart: session.programme_start,
            programStartTime: existing?.start_time,
            positionAnchorAt: session.position_anchor_at,
            playbackBaseSecs: session.playback_base_secs,
            paused: Boolean(session.paused),
          });
          const payload = {
            session_id: session.session_id,
            channel_uuid: session.channel_uuid,
            programme_start: session.programme_start,
          };
          if (positionSecs != null) {
            payload.position_secs = positionSecs;
          }
          return payload;
        });
        const programs = await getCatchupPrograms(sessions);
        if (cancelled) {
          return;
        }
        catchupProgramsRef.current = programs;
        setCatchupPrograms(programs);

        const stillOutside = timeshiftSessionsRef.current.some((session) =>
          sessionNeedsProgrammeFetch(session, programs[session.session_id])
        );
        // No next guide entry (or unknown playhead): back off instead of spinning.
        timer = setTimeout(
          stillOutside ? fetchPrograms : scheduleCheck,
          stillOutside ? 30_000 : 250
        );
      } finally {
        inFlight = false;
      }
    };

    programmeCheckRef.current = scheduleCheck;

    fetchPrograms();

    return () => {
      cancelled = true;
      clearTimer();
      programmeCheckRef.current = () => {};
    };
  }, [timeshiftProgrammeKey]);

  // Combine active streams, VOD, and catch-up into a single mixed list
  const combinedConnections = useMemo(() => {
    return getCombinedConnections(
      channelHistory,
      vodConnections,
      timeshiftSessions
    );
  }, [channelHistory, vodConnections, timeshiftSessions]);

  return (
    <>
      <Box style={{ overflowX: 'auto' }}>
        <Box miw={520}>
          <Box p={10} style={{ borderBottom: '1px solid #444' }}>
            <Group justify="space-between" align="center">
              <Title order={3}>Active Connections</Title>
              <Group align="center">
                <Text size="sm" c="dimmed">
                  {channelHistoryLength}{' '}
                  {channelHistoryLength !== 1 ? 'streams' : 'stream'} •{' '}
                  {vodConnectionsCount}{' '}
                  {vodConnectionsCount !== 1
                    ? 'VOD connections'
                    : 'VOD connection'}
                  {' • '}
                  {timeshiftConnectionsCount}{' '}
                  {timeshiftConnectionsCount !== 1
                    ? 'catch-up sessions'
                    : 'catch-up session'}
                </Text>
                <Group align="center" gap="xs">
                  <Text size="sm">Refresh Interval (seconds):</Text>
                  <NumberInput
                    value={refreshIntervalSeconds}
                    onChange={(value) => setRefreshIntervalSeconds(value || 0)}
                    min={0}
                    max={300}
                    step={1}
                    size="xs"
                    w={120}
                  />
                  {refreshIntervalSeconds === 0 && (
                    <Text size="sm" c="dimmed">
                      Refreshing disabled
                    </Text>
                  )}
                </Group>
                {isPollingActive && refreshInterval > 0 && (
                  <Text size="sm" c="dimmed">
                    Refreshing every {refreshIntervalSeconds}s
                  </Text>
                )}
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={fetchAllStats}
                  loading={false}
                >
                  Refresh Now
                </Button>
              </Group>
            </Group>
          </Box>
          <Box
            style={{
              gap: '1rem',
              gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))',
              alignContent: 'start',
            }}
            display="grid"
            p={10}
            pb={120}
            mih={'calc(100vh - 250px)'}
          >
            <Connections
              combinedConnections={combinedConnections}
              clients={clients}
              channelsByUUID={channelsByUUID}
              channels={channels}
              handleStopVODClient={handleStopVODClient}
              handleStopTimeshiftSession={handleStopTimeshiftSession}
              currentPrograms={currentPrograms}
              catchupPrograms={catchupPrograms}
            />
          </Box>
        </Box>
      </Box>

      {/* System Events Section - Fixed at bottom */}
      <Box
        style={{
          zIndex: 100,
          pointerEvents: 'none',
        }}
        pos="fixed"
        bottom={0}
        left="var(--app-shell-navbar-width, 0)"
        right={0}
        p={'0 1rem 1rem 1rem'}
      >
        <Box style={{ pointerEvents: 'auto' }}>
          <SystemEvents />
        </Box>
      </Box>
    </>
  );
};

export default StatsPage;
